import { UserX } from 'lucide-react';
import { type ReactNode, useEffect } from 'react';

import { Button } from '@/components/health-ui';
import { ErrorState } from '@/components/error-state';
import { useAuth } from '@/lib/auth-guards';
import { resetAuthRecoveryForPrincipalFault } from '@/lib/auth-redirect';
import {
  principalReadiness,
  type PrincipalReadiness,
} from '@/lib/principal-readiness';

type PrincipalGateProps = {
  children: ReactNode;
};

function unavailableDescription(readiness: PrincipalReadiness): string {
  switch (readiness) {
    case 'provider-missing-practitioner':
      return 'Your provider account is not linked to a practitioner record. Ask an administrator to complete your account setup.';
    case 'roleless':
      return 'Your account has no OmniWound account type. Ask an administrator to complete account provisioning.';
    case 'unknown-role':
      return 'Your account has an unsupported OmniWound role. Ask an administrator to correct account provisioning.';
    case 'multiple-account-types':
      return 'Your account has conflicting account types. Access is blocked until an administrator resolves the conflict.';
    case 'legacy-admin':
      return 'This legacy administrative account must be migrated to a Staff account with explicit access.';
    default:
      return 'Your account is not ready to use this application.';
  }
}

/**
 * Stops authenticated-but-unusable identities before any BFF consumer mounts.
 * Re-authentication cannot repair role or practitioner provisioning.
 */
export function PrincipalGate({
  children,
}: Readonly<PrincipalGateProps>): React.JSX.Element {
  const { forceTokenRefresh, logout, realmRoles, user } = useAuth();
  const readiness = principalReadiness(realmRoles, user?.practitionerId);

  useEffect(() => {
    if (readiness !== 'provider-ready') {
      resetAuthRecoveryForPrincipalFault();
    }
  }, [readiness]);

  if (readiness === 'provider-ready' || readiness === 'staff-account-shell') {
    return <>{children}</>;
  }

  return (
    <div
      className="min-h-screen bg-background text-foreground"
      data-testid="principal-unavailable"
      data-principal-readiness={readiness}
    >
      <ErrorState
        tone="primary"
        icon={UserX}
        headline="Account setup is incomplete"
        description={unavailableDescription(readiness)}
        actions={[
          <Button
            key="refresh-access"
            variant="outline"
            onClick={() => void forceTokenRefresh()}
          >
            Refresh access
          </Button>,
          <Button key="sign-out" onClick={() => void logout()}>
            Sign out
          </Button>,
        ]}
        compact={false}
      />
    </div>
  );
}
